import React, {useState} from 'react'

import classnames from "classnames";

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DeleteSharpIcon from "@mui/icons-material/DeleteSharp";

import Box from "@mui/material/Box";

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {Link, useLocation} from "react-router-dom";
import {useDealContext} from "../../contexts/DealContext";
import {ChevronDownIcon} from "../../theme/baseTheme";
import {CrmReturnLink} from "../../components/CrmReturnLink";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import Popover from "@mui/material/Popover";
import CircularProgress from '@mui/material/CircularProgress';
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Tooltip from "@mui/material/Tooltip";
import {useUserContext} from "../../contexts/UserContext";
import SendIcon from "@mui/icons-material/Send";
import {getQuotesDataAndPDFs} from "../Email/fetchData/getQuotesDataAndPDFs";
import {EmailModal} from "../Email/EmailModal";
import {AttachToLeadsModal} from "../CrmNext/AttachToLeadsModal";
import {DealCRMModal} from "./DealCRMPage/DealCRMModal";
import {PDFModal} from "./PDFModal";
import {DeskingModal} from "../PricingListPage/DeskingModal";
import {DealModal} from "../PricingListPage/DealModal";
import {createCashDealDirect} from "../../utils/createCashDealDirect";
import {useModalsStore} from "../../stores/modalsStore";
import {PDFDownloadLink} from "@react-pdf/renderer";
import {DealerDisclosurePDF} from "./DisclosurePDFs/Dealer/Lease/DealerDisclosurePDF";
import {PurchaseCustomerDisclosurePDF} from "./DisclosurePDFs/Customer/Purchase/PurchaseCustomerDisclosurePDF";
import {PurchaseDealerDisclosurePDF} from "./DisclosurePDFs/Dealer/Purchase/PurchaseDealerDisclosurePDF";
import {CustomerDisclosurePDF} from "./DisclosurePDFs/Customer/Lease/CustomerDisclosurePDF";
import {useFetchHook} from "../../hooks/useFetchHook";
import fetcher from "../../fetcher";
import {buildMiniQuoteSummaryString, copyQuoteHTMLTooltip} from "../../utils/CopyQuoteTooltip";
import {useCompanyLogo} from "../../hooks/useCompanyLogo";
import {buildUpdatedLabel} from "../../utils/updatedByLabel";
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import {CLEARED_RATES} from "../../utils/DeskingHelper";


// Historical wording for a delivered car. CASH has no approved copy yet, so it
// falls through to its own deal-type word rather than an invented one.
const HISTORICAL_NOUN = {LEASE: 'LEASED', FINANCE: 'FINANCED'}

// The opportunity serializes these as 'MM/DD/YYYY'. Anything else parseable is
// normalised to the same shape; anything unusable returns null so the caller
// drops the row entirely — the buildUpdatedLabel convention, no "Invalid Date".
export const formatHistoricalDate = (raw) => {
    if (!raw) return null
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(String(raw))) return String(raw)

    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) return null

    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`
}

let usePDF = () => [{url: '', loading: false}, function(){}]

if (process.env.NODE_ENV !== 'test') {
    import("@react-pdf/renderer")
        .then((mod) => {
            usePDF = mod.usePDF
        });
}

const DealHeader = ( { isModal } ) => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const { profileState, isFeatureFlagEnabled, canDesk } = useUserContext()
    const openModal = useModalsStore((state) => state.openModal)
    const [deleteDealResponse, deleteDeal] = useFetchHook(fetcher.deleteDeal)

    const [isCopied, setIsCopied] = useState(false)

    const {
        dealState,
        dealColorLight,
        cloneDeal,
        setDealStateWithoutUpdate,
        setDealStateWithoutQuoteUpdate,
        setDealStateWithUpdate,
        openListingsModal,
        isHistorical,
        currentVehicle,
        crmPerson
    } = useDealContext()
    const [isCreatingDeal, setIsCreatingDeal] = useState(false)
    const [anchorEl, setAnchorEl] = React.useState(null);
    const closeAllModals = useModalsStore((state) => state.closeAllModals)
    const {pathname} = useLocation()
    // A pop-up over that person's own page needs no way back to it.
    const crmPersonPath = crmPerson && `/crm/people/${crmPerson.id}`
    const showCrmReturn = Boolean(crmPersonPath) && pathname !== crmPersonPath

    const isPurchase = dealState.is_purchase
    const companyName = profileState?.account_name
    // null until the account's logo is confirmed loadable; until then the
    // worksheet footer renders without a logo row and the copy/paste card falls
    // back to the text companyName.
    // isCompanyLogoLoading gates the one-shot copy action below so the clipboard
    // never captures the pending-null fallback before the probe resolves. The
    // hover preview (Tooltip title) stays live and updates when the probe settles.
    const {companyLogoUrl, isCompanyLogoLoading} = useCompanyLogo()

    const handleClick = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Every "Desk as" changes the product, and its rates don't carry over: a lease
    // rate / money factor is not a finance APR, and markups differ in unit.

    const deskClonedDeal = (isPurchase) => {
        openModal({
            element: DeskingModal,
            id: "DeskingModal",
            props: {
                onSave: (data) =>  {
                    if (isTabletOrMobile) {
                        window.open(`/quotes/${data.id}`, "_self")
                    } else {
                        openModal({
                            element: DealModal,
                            id: "DealModal",
                            props: {
                                id: data.id,
                                deal_id: data.id,
                            }
                        })
                    }
                },
                ...dealState,
                is_purchase: isPurchase,
                is_one_pay: false,
                ...CLEARED_RATES,
                id: null
            }
        })
    }

    const deskCashPurchase = () => {
        const deskingData = { ...dealState, is_purchase: true, is_one_pay: true, ...CLEARED_RATES, id: null }
        const onSave = (data) => {
            if (isTabletOrMobile) {
                window.open(`/quotes/${data.id}`, "_self")
            } else {
                openModal({
                    element: DealModal,
                    id: "DealModal",
                    props: {
                        id: data.id,
                        deal_id: data.id
                    }
                })
            }
        }
        const openGrid = () => openModal({
            element: DeskingModal,
            id: "DeskingModal",
            props: { onSave, ...deskingData }
        })

        handleClose()
        setIsCreatingDeal(true)
        // Cash: skip the "Quotes for…" grid — scan, auto-pick the cash quote,
        // create the deal, and open the cash quote page directly.
        createCashDealDirect(deskingData, { onSave, onNeedsGrid: openGrid })
            .catch(() => {})
            .finally(() => setIsCreatingDeal(false))
    }

    const cloneDealAndOpenModal = () => {
        return cloneDeal(dealState).then((data) => {
            openModal({
                element: DealModal,
                id: "DealModal",
                props: {id: data.data.id},
            })
        })
    }

    const cloneIcon = () => {
        let icon;
        if (isCreatingDeal) {
            icon = <CircularProgress size={16} color="inherit"/>
        } else {
            if (isPurchase) {
                icon = <AddOutlinedIcon className={'isPurchase'}/>
            } else {
                icon = <AccountBalanceIcon/>
            }
        }
        return icon
    }

    const sendEmailQuote = () => {
        openModal({
            element: EmailModal,
            id: "EmailModal",
            props: {
                header: `Check out this Quote`,
                getContent: () => getQuotesDataAndPDFs([dealState.id], "", profileState.account_name, dealState, companyLogoUrl),
                to: "",
                from: profileState.user_email,
                subject: `Lease Quotes from ${profileState.account_name}`,
            }
        })
    }


    const copyQuoteToClipBoardHTML = (isDealer) => {
        if (isCopied) {
            return (
                <div>Copied!</div>
            )
        }

        return (
            <div dangerouslySetInnerHTML={{ __html: copyQuoteHTMLTooltip(dealState, companyName, isDealer, companyLogoUrl) }} />
        )
    }
    const copyQuoteToClipBoard = async (isDealer) => {
        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([copyQuoteHTMLTooltip(dealState, companyName, isDealer, companyLogoUrl)], { type: 'text/html' }),
                    'text/plain': new Blob([buildMiniQuoteSummaryString(dealState, isDealer)], { type: 'text/plain' })
                })
            ]);
            setIsCopied(true)
            setTimeout(() =>  {
                setIsCopied(false)
            }, 2000)
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    if (!dealState) return null;

    const updatedLabel = buildUpdatedLabel(dealState)
    const expirationDate = dealState.formatted_quote.expiration_date
    // Lender-program expiry. A car delivered years ago will always sit on an
    // expired program, and there is nothing to re-price, so the warning is
    // noise on a historical record.
    const isExpired = !isHistorical
        && new Date(expirationDate).setUTCHours(0,0,0,0) <= new Date().setUTCHours(0,0,0,0)
    const isViewModeOnly = dealState._is_view_mode_only
    const isLocked = dealState.is_locked

    const isCashPurchase = dealState.is_purchase && dealState.is_one_pay

    const headerTitle = {
        true: 'LEASE',
        [dealState.is_purchase]: 'FINANCE',
        [isCashPurchase]: 'CASH'
    }.true

    const inceptionDate = formatHistoricalDate(currentVehicle?.inception_date)
    const maturityDate = formatHistoricalDate(currentVehicle?.maturity_date)

    // A historical record's Tools menu is only the two worksheet items — and
    // partner-API quotes don't get those either, since for them "worksheet"
    // opens a re-desk. That empties the menu, so the button goes with it.
    const isPartnerApi = dealState.formatted_quote.source === 'PARTNER_API'
    const canShowWorksheets = !(isHistorical && isPartnerApi)

    const getPDFDoc = (isCustomerPDF) => {
        if (dealState.is_purchase && isCustomerPDF) {
            return (<PurchaseCustomerDisclosurePDF
                dealState={dealState}
                customerName={''}
                customerAddress={''}
                companyName={profileState?.account_name}
                companyLogoUrl={companyLogoUrl}
            />)
        } else if (dealState.is_purchase && !isCustomerPDF) {
            return (
                <PurchaseDealerDisclosurePDF
                    dealState={dealState}
                    companyName={profileState?.account_name}
                    companyLogoUrl={companyLogoUrl}
                />
            )
        } else if (!dealState.is_purchase && isCustomerPDF) {
            return (
                <CustomerDisclosurePDF
                    dealState={dealState}
                    customerName={''}
                    customerAddress={''}
                    companyName={profileState?.account_name}
                    companyLogoUrl={companyLogoUrl}
                />
            )
        } else if (!dealState.is_purchase && !isCustomerPDF) {
            return (
                <DealerDisclosurePDF
                    dealState={dealState}
                    companyName={profileState?.account_name}
                    companyLogoUrl={companyLogoUrl}
                />
            )
        }
    }

    const openPDFModal = (isCustomerPDF) => {
        // The item isn't rendered in that case (see canShowWorksheets); this
        // makes the re-desk unreachable rather than merely hidden.
        if (isHistorical && isPartnerApi) return
        if (dealState.formatted_quote.source === 'PARTNER_API') {
            openModal({
                id: "DeskingModal",
                element: DeskingModal,
                props: {
                    id: dealState.id,
                    deal_id: dealState.id,
                    ...dealState,
                    onSave: (updateData) => setDealStateWithUpdate(updateData, false),
                }
            })
            return
        }
        openModal({
            element: PDFModal,
            id: "PDFModal",
            props: {
                isCustomerPDF: isCustomerPDF,
                dealState: dealState
            }
        })
    }

    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className={'DealPage-header'}
            >
                <Stack direction="row" alignItems="center" gap={'16px'} sx={{minWidth: 0}}>
                    {
                        // A CRM quote leads with its person. The click also
                        // closes pop-ups, which outlast the route change.
                        showCrmReturn &&
                        <CrmReturnLink person={crmPerson} onClick={closeAllModals}/>
                    }
                    <Typography
                        variant="h5"
                        component="div"
                        className={'header-title'}
                    >
                        {
                            isHistorical
                                ? `${HISTORICAL_NOUN[headerTitle] || headerTitle} VEHICLE`
                                : `${headerTitle} ${isTabletOrMobile ? '' : 'QUOTE'}`
                        }
                        {
                            // Historical keeps the marker but not the link: the
                            // record has no live worksheet to open.
                            isModal && (isHistorical
                                ? <span className={'header-title-static'} aria-hidden="true"><OpenInNewOutlinedIcon/></span>
                                : <Link to={`/quotes/${dealState.id}`} target="_blank" rel="noopener noreferrer"><OpenInNewOutlinedIcon/></Link>)
                        }
                        {
                            isViewModeOnly && dealState.created_by_username &&
                            <span style={{ fontWeight: 400, fontSize: '14px', color: 'var(--text-secondary)', marginLeft: '8px' }}>
                                Created by &ldquo;{dealState.created_by_username}&rdquo;
                            </span>
                        }
                        {
                            !isTabletOrMobile && !isHistorical && updatedLabel &&
                            <Typography
                                variant="subtitle1"
                                component="div"
                                className={'header-subtitle press-enter'}
                            >
                                {updatedLabel}
                            </Typography>
                        }
                        {
                            isExpired &&
                            <Typography
                                variant="subtitle1"
                                component="div"
                                sx={{
                                    fontSize: '12px',
                                    color: '#A23325',
                                    marginLeft: '10px'
                                }}
                            >
                                <WarningAmberRoundedIcon/>
                                This program is expired.
                            </Typography>
                        }
                    </Typography>
                </Stack>
                {
                    // The sale's own dates stand in for "Updated by". They sit
                    // between the title and Tools rather than inside the title,
                    // so they centre in the gap the two leave; each appears only
                    // when it has a date.
                    isHistorical && (inceptionDate || maturityDate) &&
                    <div className={'header-historical-dates'}>
                        {inceptionDate && <span>Inception Date {inceptionDate}</span>}
                        {maturityDate && <span>Expiration Date {maturityDate}</span>}
                    </div>
                }
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    {
                        false && isFeatureFlagEnabled('crm') && !isTabletOrMobile &&
                        <div
                            className={'lock-icon'}
                        >
                            {
                                isLocked ?
                                    <Tooltip
                                        title={
                                            <Typography color="inherit">
                                                This deal belongs to a client(s) and is locked. It cannot be updated unless you unlock it by clicking the lock icon.
                                            </Typography>
                                        }
                                    >
                                        <LockIcon
                                            onClick={() => setDealStateWithoutUpdate({...dealState, is_locked: false})}
                                        />
                                    </Tooltip>
                                    :
                                    <LockOpenIcon
                                        onClick={() => setDealStateWithoutUpdate({...dealState, is_locked: true})}
                                    />
                            }
                        </div>
                    }
                    {
                    // Copy-for-dealer, copy-for-customer and attach-to-lead all
                    // act on a quote still being worked. A delivered car isn't.
                    !isHistorical &&
                    <React.Fragment>
                    {
                        // Leftmost: picking who a quote is for comes before
                        // copying it out. Opens the CRM pipeline over this page
                        // rather than navigating — the rep keeps their quote.
                        isFeatureFlagEnabled('pipeline') && !isTabletOrMobile &&
                        <Tooltip title={'Attach this quote to a lead'} arrow placement={'bottom'}>
                            <div
                                className={'header-button crm'}
                                style={{ marginRight: 8 }}
                                onClick={() => {
                                    openModal({
                                        element: AttachToLeadsModal,
                                        id: "AttachToLeadsModal",
                                        props: {
                                            dealId: dealState.id
                                        }
                                    })
                                }}
                            >
                                <PersonAddAlt1Icon/>
                            </div>
                        </Tooltip>
                    }
                    <Tooltip
                        title={copyQuoteToClipBoardHTML(true)}
                        arrow placement={'bottom'}
                        slotProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: '#FFFFFF',
                                    color: 'rgba(0, 0, 0, 0.87)',
                                    padding: isCopied ? '5px': '0px',
                                    fontSize: 12,
                                    border: isCopied ? '1px solid #dadde9': '',
                                }
                            },
                            arrow: {
                                sx: {
                                    color: '#FFFFFF', // Arrow fill color
                                    '&::before': {
                                        border: isCopied ? '1px solid #dadde9': '', // Arrow border
                                    }
                                }
                            }
                        }}
                    >
                        <button
                            type="button"
                            className={'topbar-icon-btn'}
                            onClick={() => copyQuoteToClipBoard(true)}
                            disabled={isCompanyLogoLoading}
                            style={{ marginRight: 8 }}
                        >
                            <FileCopyIcon/>
                        </button>
                    </Tooltip>
                    <Tooltip
                        title={copyQuoteToClipBoardHTML(false)}
                        arrow placement={'bottom'}
                        slotProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: '#FFFFFF',
                                    color: 'rgba(0, 0, 0, 0.87)',
                                    padding: isCopied ? '5px': '0px',
                                    fontSize: 12,
                                    border: isCopied ? '1px solid #dadde9': '',
                                }
                            },
                            arrow: {
                                sx: {
                                    color: '#FFFFFF', // Arrow fill color
                                    '&::before': {
                                        border: isCopied ? '1px solid #dadde9': '', // Arrow border
                                    }
                                }
                            }
                        }}
                    >
                        <button
                            type="button"
                            className={'topbar-icon-btn'}
                            onClick={() => copyQuoteToClipBoard(false)}
                            disabled={isCompanyLogoLoading}
                            style={{ marginRight: 8 }}
                        >
                            <ContentCopyIcon/>
                        </button>
                    </Tooltip>
                    {
                        // Legacy CRM attach; Pipeline accounts use the person+ above.
                        isFeatureFlagEnabled('crm') && !isFeatureFlagEnabled('pipeline') && !isTabletOrMobile &&
                        <div
                            className={'header-button crm'}
                            onClick={() => {
                                openModal({
                                    element: DealCRMModal,
                                    id: "DealCRMModal",
                                    props: {
                                        initialSelectedLeads: dealState.leads,
                                        initialSelectedClients: dealState.clients,
                                        onLeadsSave: (leadIds) => {
                                            setDealStateWithoutQuoteUpdate({leads: leadIds})
                                        },
                                        onClientsSave: (clientIds) => {
                                            setDealStateWithoutQuoteUpdate({clients: clientIds})
                                        }
                                    }
                                })
                            }}
                        >
                            <GroupAddIcon/>
                        </div>
                    }
                    </React.Fragment>
                    }

                    {
                        // Historical + partner-API leaves the menu empty, so the
                        // button goes with its only remaining items.
                        canShowWorksheets &&
                        <div
                            onClick={handleClick}
                            className={'header-button tools'}
                        >
                            Tools
                            <ChevronDownIcon className={classnames('tools-chevron', {open: Boolean(anchorEl)})}/>
                        </div>
                    }
                    <Popover
                        onClick={(e) => e.stopPropagation()}
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            horizontal: 'right',
                            vertical: 'top'
                        }}
                        className={'deal-header-dropdown-menu-container'}
                    >
                        <Box className={'deal-header-dropdown-menu'}>
                            {
                                isTabletOrMobile && canShowWorksheets &&
                                <React.Fragment>
                                    <PDFDownloadLink document={getPDFDoc(true)}>
                                        {({url}) => (
                                            <a
                                                href={url}
                                                target="_blank"
                                                className={classnames('header-button', {
                                                    isPurchase: isPurchase
                                                })}
                                            >
                                                <span
                                                    style={{marginRight: '20px'}}
                                                >
                                                    <PictureAsPdfIcon
                                                        className={classnames('', {
                                                            isPurchase: isPurchase
                                                        })}
                                                    />
                                                 </span>
                                                <span>Customer Worksheet</span>
                                            </a>
                                        )}
                                    </PDFDownloadLink>
                                    <PDFDownloadLink document={getPDFDoc(false)}>
                                        {({url}) => (
                                            <a
                                                href={url}
                                                target="_blank"
                                                className={classnames('header-button', {
                                                    isPurchase: isPurchase
                                                })}
                                            >
                                                        <span
                                                            style={{marginRight: '20px'}}
                                                        >
                                                            <PictureAsPdfIcon
                                                                className={classnames('', {
                                                                    isPurchase: isPurchase
                                                                })}
                                                            />
                                                         </span>
                                                <span>Dealer Worksheet</span>
                                            </a>
                                        )}
                                    </PDFDownloadLink>
                                </React.Fragment>
                            }
                            {
                                !isTabletOrMobile && canShowWorksheets &&
                                <React.Fragment>
                                    <a
                                        onClick={() => openPDFModal(true)}
                                        className={classnames('header-button', {
                                            isPurchase: isPurchase
                                        })}
                                    >

                                    <span
                                        style={{marginRight: '20px'}}
                                    >
                                        <PictureAsPdfIcon
                                            className={classnames('', {
                                                isPurchase: isPurchase
                                            })}
                                        />
                                     </span>
                                        <span>Customer Worksheet</span>
                                    </a>
                                    <a
                                        onClick={() => openPDFModal(false)}
                                        className={classnames('header-button', {
                                            isPurchase: isPurchase
                                        })}
                                    >
                                            <span
                                                style={{marginRight: '20px'}}
                                            >
                                                <PictureAsPdfIcon
                                                    className={classnames('', {
                                                        isPurchase: isPurchase
                                                    })}
                                                />
                                             </span>
                                        <span>Dealer Worksheet</span>
                                    </a>
                                </React.Fragment>
                            }

                            {
                            // Everything past the worksheets creates, re-desks,
                            // emails or deletes the deal. None of it belongs on
                            // a record of a car already delivered.
                            !isHistorical &&
                            <React.Fragment>
                            {
                                isFeatureFlagEnabled('listings') &&
                                <div
                                    onClick={openListingsModal}
                                    className={classnames('header-button', {
                                        isPurchase: isPurchase
                                    })}
                                >
                                    <span
                                        style={{marginRight: '20px'}}
                                    >
                                        <DirectionsCarOutlinedIcon className={classnames('', {
                                            isPurchase: isPurchase
                                        })}/>
                                    </span>
                                    <span>
                                        Find Inventory
                                    </span>
                                </div>
                            }

                            {
                            // Each of these re-prices the car under a different
                            // structure, and Clone Quote creates a second priced
                            // deal — all four end in a scan, so all four are gone
                            // for a role that may not run one. The worksheets,
                            // Find Inventory, Send Email and Delete stay: they
                            // read the quote that already exists.
                            canDesk() &&
                            <React.Fragment>
                            <div
                                onClick={() => deskClonedDeal(!dealState.is_purchase)}
                                className={classnames('header-button', {
                                    isPurchase: isPurchase
                                })}
                            >
                                <span
                                    style={{marginRight: '20px'}}
                                >
                                    {cloneIcon()}
                                </span>
                                <span>
                                    Desk as {isPurchase ? 'Lease ' : 'Finance '}
                                </span>
                            </div>
                            {
                                isCashPurchase &&
                                <div
                                    onClick={() => deskClonedDeal(true)}
                                    className={classnames('header-button', {
                                        isPurchase: isPurchase
                                    })}
                                >
                                <span
                                    style={{marginRight: '20px'}}
                                >
                                    <AccountBalanceIcon className={classnames('', {
                                        isPurchase: isPurchase || isCashPurchase
                                    })}
                                    />
                                </span>
                                    <span>
                                    Desk as Finance
                                </span>
                                </div>
                            }
                            {
                                !isCashPurchase &&
                                <div
                                    onClick={deskCashPurchase}
                                    className={classnames('header-button', {
                                        isPurchase: isPurchase
                                    })}
                                >
                                <span
                                    style={{marginRight: '20px'}}
                                >
                                    <AttachMoneyIcon className={classnames('', {
                                        isPurchase: isPurchase
                                    })}
                                    />
                                </span>
                                    <span>
                                    Desk as Cash
                                </span>
                                </div>
                            }

                            <div
                                onClick={cloneDealAndOpenModal}
                                className={classnames('header-button', {
                                    isPurchase: isPurchase
                                })}
                            >
                                <span
                                    style={{marginRight: '20px'}}
                                >
                                    <ContentCopyIcon className={classnames('', {
                                        isPurchase: isPurchase
                                    })}/>
                                </span>
                                <span>
                                    Clone Quote
                                </span>
                            </div>
                            </React.Fragment>
                            }

                            <div
                                onClick={sendEmailQuote}
                                className={classnames('header-button', {
                                    isPurchase: isPurchase
                                })}
                            >
                                <span
                                    style={{marginRight: '20px'}}
                                >
                                    <SendIcon className={classnames('', {
                                        isPurchase: isPurchase
                                    })}/>
                                </span>
                                <span>
                                    Send Email
                                </span>
                            </div>
                            <div
                                onClick={() => {
                                    return deleteDeal(dealState.id).then(() => {
                                        window.location.replace("/quotes")
                                    })
                                }}
                                className={classnames('header-button', {
                                    isPurchase: isPurchase
                                })}
                            >
                                <span
                                    style={{marginRight: '20px'}}
                                >
                                    <DeleteSharpIcon style={{color: "#A23325"}}/>
                                </span>
                                <span>
                                    Delete Quote
                                </span>
                            </div>
                            </React.Fragment>
                            }
                        </Box>
                    </Popover>
                </Stack>
            </Stack>
        </Box>
    );
};

export default DealHeader;